import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { Ticket } from '../api';
import { agoLabel } from '../relativeTime';
import { actorLabel, recentlyTouched, type Touched } from '../recent';
import { STATUS_COLOUR } from './status';

/**
 * One line of it: when, who, and the ticket.
 */
function RecentRow({
  row: { ticket, at, kind, actor },
  now,
  onOpen,
}: {
  row: Touched;
  /** Passed in rather than read here, so no two rows in one paint disagree about the time. */
  now: number;
  onOpen: (ref: number) => void;
}) {
  return (
    <TableRow hover onClick={() => onOpen(ticket.ref)} sx={{ cursor: 'pointer' }}>
      {/* The exact stamp on hover: the relative label is what you read, but "was that
          before or after the deploy?" needs the real thing.

          With no usable stamp the KIND is suppressed too. Which change ranked the ticket
          is decided by comparing the stamps, so when none of them parses there is no more
          basis for saying "created" than there was for saying 1970. */}
      <TableCell
        sx={{ whiteSpace: 'nowrap' }}
        title={at === null ? 'No usable timestamp on this ticket' : new Date(at).toISOString()}
      >
        <Typography variant="body2" component="span">
          {at === null ? 'unknown' : agoLabel(now - at)}
        </Typography>
        {at !== null && (
          <Typography variant="body2" component="span" color="text.secondary">
            {` · ${kind}`}
          </Typography>
        )}
      </TableCell>
      {/* Blank only for a ticket with no history to read — a migrated one, ranked on
          `createdAt`/`updatedAt`, which date a change without recording who made it.
          The full address is the title; the cell shows the mailbox. */}
      <TableCell sx={{ whiteSpace: 'nowrap' }} title={actor ?? undefined}>
        <Typography variant="body2" component="span" color="text.secondary">
          {actor === null ? '' : actorLabel(actor)}
        </Typography>
      </TableCell>
      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        DWLF-{ticket.ref}
      </TableCell>
      <TableCell sx={{ minWidth: 280 }}>{ticket.name}</TableCell>
      <TableCell>
        <Chip size="small" label={ticket.status} color={STATUS_COLOUR[ticket.status] ?? 'default'} />
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{ticket.epic}</TableCell>
    </TableRow>
  );
}

/**
 * The board in the order things happened, rather than the order they were filed.
 *
 * Same filtered set as the Table — Epic and Status apply here too, so the tab is a
 * different ORDER over the same question, not a different question.
 */
export function RecentView({
  tickets,
  onOpen,
}: {
  tickets: Ticket[];
  onOpen: (ref: number) => void;
}) {
  const { rows, total } = recentlyTouched(tickets);
  // Once per render rather than per row, so no two rows in one paint can disagree about
  // what time it is. Re-rendered by the board's 30s poll, which is what keeps it moving.
  const now = Date.now();

  if (rows.length === 0) {
    return (
      <Typography sx={{ p: 3 }} color="text.secondary">
        {tickets.length === 0
          ? 'Nothing matches this filter.'
          : 'Everything matching this filter is archived, and archived tickets are left out here.'}
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Changed', 'By', 'Ref', 'Name', 'Status', 'Epic'].map((head) => (
              <TableCell key={head} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                {head}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <RecentRow key={row.ticket.ref} row={row} now={now} onOpen={onOpen} />
          ))}
        </TableBody>
      </Table>

      {/*
        A truncated list that looks complete is what HR-2 is about, so the count says what
        it is twenty OF. What the ordering counts as a change is stated for the same
        reason: the two exclusions below are the only ways this list can differ from what
        a reader expects, and an unexplained absence reads as nothing having happened.
      */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2 }}>
        {total > rows.length &&
          `The ${rows.length} most recently changed of ${total} matching tickets. `}
        Comments and links count as changes, alongside edits to a ticket&rsquo;s own fields.
        Archived tickets are not listed, though the Table tab shows them.
      </Typography>
    </Box>
  );
}
